import { useEffect, useRef, useState } from "react";
import { UploadCloud, Video } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

type TipoVideo = "gravacao" | "devolucao";

function normalizeCodBip(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.includes("@id@>") || trimmed.includes("@Id@>") || trimmed.includes("@ID@>")) {
    const match = trimmed.match(/@(?:id|Id|ID)@>@?([^@,]+)@?/);
    if (match && match[1].trim() !== "") return match[1].trim();
  }
  if (trimmed.includes("^id^Ç^") || trimmed.includes("^Id^Ç^") || trimmed.includes("^ID^Ç^")) {
    const match = trimmed.match(/\^(?:id|Id|ID)\^Ç\^([^^,]+)/);
    if (match && match[1].trim() !== "") return match[1].trim();
  }
  const repeatedMatch = trimmed.match(/^([^,]+),\1(?:,[^,]*)?$/);
  if (repeatedMatch && repeatedMatch[1].trim() !== "") return repeatedMatch[1].trim();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const id = parsed["id"] ?? parsed["Id"] ?? parsed["ID"];
        if (id !== undefined && id !== null && String(id).trim() !== "") return String(id).trim();
      }
    } catch { /* não é JSON válido */ }
  }
  return trimmed;
}

async function uploadWithRetry(formData: FormData, maxRetries = 3): Promise<string> {
  let retries = 0;
  while (retries < maxRetries) {
    try {
      const response = await fetch("/api/upload", { method: "POST", body: formData });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      if (result.url) return result.url;
      throw new Error("Upload retornou sem URL");
    } catch (error) {
      retries++;
      if (retries >= maxRetries) throw error;
      await new Promise((resolve) => setTimeout(resolve, 1000 * retries));
    }
  }
  throw new Error("Upload falhou após retries");
}

async function obterDuracaoVideo(arquivo: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(arquivo);
    const video = document.createElement("video");
    let finalizado = false;
    let buscouFim = false;
    const limpar = () => {
      video.onloadedmetadata = null;
      video.onseeked = null;
      video.onerror = null;
      video.removeAttribute("src");
      video.load();
      URL.revokeObjectURL(url);
    };
    const concluir = (duracao: number) => {
      if (finalizado) return;
      finalizado = true;
      limpar();
      resolve(duracao);
    };
    const falhar = (erro: Error) => {
      if (finalizado) return;
      finalizado = true;
      limpar();
      reject(erro);
    };
    const lerDuracao = () => {
      const duracao = Math.ceil(video.duration);
      if (Number.isFinite(duracao) && duracao > 0) return concluir(duracao);
      if (buscouFim) return falhar(new Error("Não foi possível identificar a duração do vídeo."));
      buscouFim = true;
      try {
        video.currentTime = Number.MAX_SAFE_INTEGER;
      } catch {
        falhar(new Error("Não foi possível identificar a duração do vídeo."));
      }
    };

    video.onloadedmetadata = lerDuracao;
    video.onseeked = lerDuracao;
    video.onerror = () => {
      falhar(new Error("Não foi possível ler o vídeo selecionado."));
    };
    video.preload = "metadata";
    video.src = url;
  });
}

export default function UploadVideosMecom() {
  const [tipoVideo, setTipoVideo] = useState<TipoVideo>("gravacao");
  const [pontoOperacionalId, setPontoOperacionalId] = useState<string>(() => localStorage.getItem("mecom_upload_videos_pontoId") || "__none__");
  const [codigoBip, setCodigoBip] = useState("");
  const [pedidoSelecionado, setPedidoSelecionado] = useState<{ codigoBip: string; status: string } | null>(null);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const codigoInputRef = useRef<HTMLInputElement>(null);
  const arquivoInputRef = useRef<HTMLInputElement>(null);

  const { data: pontosOperacionais } = trpc.pontosOperacionais.list.useQuery();
  const gravacaoMutation = trpc.mecom.bipGravacao.useMutation();
  const devolucaoMutation = trpc.mecom.bipDevolucao.useMutation();

  useEffect(() => {
    localStorage.setItem("mecom_upload_videos_pontoId", pontoOperacionalId);
  }, [pontoOperacionalId]);

  const verificarPedido = async () => {
    const codigoNormalizado = normalizeCodBip(codigoBip);
    if (!codigoNormalizado) return;

    setIsChecking(true);
    try {
      const response = await fetch(`/api/trpc/mecom.checkPedido?input=${encodeURIComponent(JSON.stringify({ json: { codigoBip: codigoNormalizado } }))}`, { credentials: "include" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const body = await response.json();
      const pedido = body?.result?.data?.json;

      if (!pedido?.exists) {
        toast.error(`Pedido não encontrado: ${codigoNormalizado}. Realize a entrada primeiro.`);
        setPedidoSelecionado(null);
        return;
      }
      if (tipoVideo === "gravacao" && pedido.status === "gravado") {
        toast.error(`Pedido ${codigoNormalizado} já foi gravado.`);
        setPedidoSelecionado(null);
        return;
      }
      if (tipoVideo === "gravacao" && pedido.status === "expedido") {
        toast.error(`Pedido ${codigoNormalizado} já foi expedido. Não é possível gravar.`);
        setPedidoSelecionado(null);
        return;
      }
      if (pedido.status === "devolvido") {
        toast.error(`Pedido ${codigoNormalizado} já foi devolvido.`);
        setPedidoSelecionado(null);
        return;
      }

      setCodigoBip(codigoNormalizado);
      setPedidoSelecionado({ codigoBip: codigoNormalizado, status: pedido.status });
      toast.success(`Pedido selecionado: ${codigoNormalizado}`);
    } catch (error: any) {
      toast.error(`Erro ao verificar pedido: ${error.message}`);
      setPedidoSelecionado(null);
    } finally {
      setIsChecking(false);
    }
  };

  const enviarVideo = async () => {
    if (pontoOperacionalId === "__none__") {
      toast.error("Selecione um ponto operacional antes de enviar.");
      return;
    }
    if (!pedidoSelecionado) {
      toast.error("Digite ou bipe um pedido MECOM e selecione-o antes de enviar.");
      return;
    }
    if (!arquivo) {
      toast.error("Selecione um vídeo para enviar.");
      return;
    }

    setIsUploading(true);
    try {
      const duration = await obterDuracaoVideo(arquivo);
      const formData = new FormData();
      formData.append("file", arquivo, arquivo.name);
      const videoUrl = await uploadWithRetry(formData, 3);
      const dados = {
        codigoBip: pedidoSelecionado.codigoBip,
        pontoOperacionalId: Number(pontoOperacionalId),
        videoUrl,
        duration,
        fileSize: arquivo.size,
      };

      if (tipoVideo === "gravacao") {
        await gravacaoMutation.mutateAsync(dados);
      } else {
        await devolucaoMutation.mutateAsync(dados);
      }

      toast.success(`Vídeo de ${tipoVideo === "gravacao" ? "gravação" : "devolução"} salvo: ${pedidoSelecionado.codigoBip}`);
      setCodigoBip("");
      setPedidoSelecionado(null);
      setArquivo(null);
      if (arquivoInputRef.current) arquivoInputRef.current.value = "";
      codigoInputRef.current?.focus();
    } catch (error: any) {
      toast.error(`Erro ao salvar vídeo: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-4">
      <h1 className="text-2xl font-bold">Upload Vídeos MECOM</h1>

      <div className="space-y-4 rounded-lg border p-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium">Tipo de vídeo</label>
            <Select value={tipoVideo} onValueChange={(value) => {
              setTipoVideo(value as TipoVideo);
              setPedidoSelecionado(null);
            }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="gravacao">Gravação</SelectItem>
                <SelectItem value="devolucao">Devolução</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Ponto operacional</label>
            <Select value={pontoOperacionalId} onValueChange={setPontoOperacionalId}>
              <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Selecione...</SelectItem>
                {(pontosOperacionais as any[] || []).map((ponto: any) => (
                  <SelectItem key={ponto.id} value={String(ponto.id)}>{ponto.tipo} - #{ponto.id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Pedido MECOM</label>
          <div className="flex gap-2">
            <Input
              ref={codigoInputRef}
              value={codigoBip}
              onChange={(event) => {
                setCodigoBip(event.target.value);
                setPedidoSelecionado(null);
              }}
              onKeyDown={(event) => event.key === "Enter" && verificarPedido()}
              placeholder="Digite ou bipe o código do pedido"
              className="font-mono"
              autoFocus
            />
            <Button onClick={verificarPedido} disabled={isChecking || isUploading}>
              <Video className="w-4 h-4 mr-1" /> Selecionar
            </Button>
          </div>
          {pedidoSelecionado && (
            <p className="text-sm text-green-700">Pedido selecionado: <span className="font-mono">{pedidoSelecionado.codigoBip}</span> — status atual: {pedidoSelecionado.status}</p>
          )}
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Arquivo de vídeo</label>
          <Input
            ref={arquivoInputRef}
            type="file"
            accept="video/*"
            onChange={(event) => setArquivo(event.target.files?.[0] || null)}
            disabled={isUploading}
          />
          {arquivo && <p className="text-sm text-muted-foreground">{arquivo.name}</p>}
        </div>

        <Button onClick={enviarVideo} disabled={isUploading || isChecking}>
          <UploadCloud className="w-4 h-4 mr-1" /> {isUploading ? "Enviando vídeo..." : "Enviar vídeo"}
        </Button>
      </div>
    </div>
  );
}
